/* eslint-disable no-console */
import { db } from "@/firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp
} from "firebase/firestore";

const state = {
  databuckets: [],
  loadingDatabuckets: false,
  databucketTotals: [] // used for tiers and badges
};

const actions = {
  async loadDatabuckets({ commit, getters }) {
    commit("setLoadingDatabuckets", true);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const databucketsRef = collection(programRef, "databuckets");
      const databucketsQuery = query(databucketsRef, orderBy("titleUppercase"));
      const querySnapshot = await getDocs(databucketsQuery);

      const databuckets = [];
      querySnapshot.forEach(docSnapshot => {
        const data = docSnapshot.data();
        databuckets.push({
          id: docSnapshot.id,
          ...data,
          created: data.created?.toDate() || new Date(),
          updated: data.updated?.toDate() || new Date()
        });
      });

      commit("setDatabuckets", databuckets);
    } catch (error) {
      console.error("Error loading databuckets:", error);
      commit("setDatabuckets", []);
    } finally {
      commit("setLoadingDatabuckets", false);
    }
  },

  async loadActiveDatabuckets({ commit, getters }) {
    commit("setLoadingDatabuckets", true);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const databucketsRef = collection(programRef, "databuckets");
      const databucketsQuery = query(
        databucketsRef,
        where("status", "==", "active"),
        orderBy("titleUppercase")
      );
      const querySnapshot = await getDocs(databucketsQuery);

      const databuckets = [];
      querySnapshot.forEach(docSnapshot => {
        const data = docSnapshot.data();
        databuckets.push({
          id: docSnapshot.id,
          ...data,
          created: data.created?.toDate() || new Date(),
          updated: data.updated?.toDate() || new Date()
        });
      });

      commit("setDatabuckets", databuckets);
    } catch (error) {
      console.error("Error loading active databuckets:", error);
      commit("setDatabuckets", []);
    } finally {
      commit("setLoadingDatabuckets", false);
    }
  },

  async getDatabucketById({ getters }, databucketId) {
    if (!databucketId) {
      console.error("Databucket ID is required");
      return null;
    }

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const databucketsRef = collection(programRef, "databuckets");
      const databucketRef = doc(databucketsRef, databucketId);
      const databucketDoc = await getDoc(databucketRef);

      if (databucketDoc.exists()) {
        const data = databucketDoc.data();
        return {
          id: databucketDoc.id,
          ...data,
          created: data.created?.toDate() || new Date(),
          updated: data.updated?.toDate() || new Date()
        };
      } else {
        console.warn(`Databucket with ID ${databucketId} not found`);
        return null;
      }
    } catch (error) {
      console.error("Error getting databucket by ID:", error);
      return null;
    }
  },

  async createDatabucket({ dispatch, commit, getters }, payload) {
    if (!payload || !payload.title) {
      throw new Error("Databucket payload with title is required");
    }

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const databucketsRef = collection(programRef, "databuckets");

      // Check for duplicate titles
      const titleDupQuery = query(
        databucketsRef,
        where(
          "titleUppercase",
          "==",
          payload.titleUppercase || payload.title.toUpperCase()
        )
      );
      const titleDupSnapshot = await getDocs(titleDupQuery);

      if (!titleDupSnapshot.empty) {
        throw new Error("Title is already registered");
      }

      const databucket = {
        ...payload,
        titleUppercase: payload.titleUppercase || payload.title.toUpperCase(),
        createdBy: getters.user?.id || "unknown",
        updatedBy: getters.user?.id || "unknown",
        created: serverTimestamp(),
        updated: serverTimestamp()
      };

      const newDatabucketRef = await addDoc(databucketsRef, databucket);

      // Note: server time is unavailable until we refetch.
      const tmpDatabucket = {
        ...databucket,
        id: newDatabucketRef.id,
        created: new Date(),
        updated: new Date()
      };

      commit("createDatabucket", tmpDatabucket);
      dispatch("setSnackbar", "Databucket Created.");

      return tmpDatabucket;
    } catch (error) {
      console.error("Error creating databucket:", error);
      throw error.message || "Error occurred when creating databucket";
    }
  },

  async updateDatabucket({ commit, getters, dispatch }, { id, updates }) {
    if (!id || !updates) {
      throw new Error("Databucket ID and updates are required");
    }

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const databucketsRef = collection(programRef, "databuckets");
      const databucketRef = doc(databucketsRef, id);

      // If title is being updated, check for duplicates
      if (updates.title) {
        const titleUppercase = updates.title.toUpperCase();
        const titleDupQuery = query(
          databucketsRef,
          where("titleUppercase", "==", titleUppercase)
        );
        const titleDupSnapshot = await getDocs(titleDupQuery);

        let isDuplicate = false;
        titleDupSnapshot.forEach(docSnapshot => {
          if (docSnapshot.id !== id) {
            isDuplicate = true;
          }
        });

        if (isDuplicate) {
          throw new Error("Title is already registered");
        }

        updates.titleUppercase = titleUppercase;
      }

      const updateData = {
        ...updates,
        updatedBy: getters.user?.id || "unknown",
        updated: serverTimestamp()
      };

      await updateDoc(databucketRef, updateData);

      // Reload databuckets to get updated list
      await dispatch("loadDatabuckets");

      dispatch("setSnackbar", "Databucket updated successfully.");
      return true;
    } catch (error) {
      console.error("Error updating databucket:", error);
      throw error.message || "Error occurred when updating databucket";
    }
  },

  async deleteDatabucket({ commit, getters, dispatch }, databucketId) {
    if (!databucketId) {
      throw new Error("Databucket ID is required");
    }

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const databucketsRef = collection(programRef, "databuckets");
      const databucketRef = doc(databucketsRef, databucketId);

      await deleteDoc(databucketRef);

      // Remove from local state
      commit("deleteDatabucket", databucketId);
      dispatch("setSnackbar", "Databucket deleted successfully.");

      return true;
    } catch (error) {
      console.error("Error deleting databucket:", error);
      throw error.message || "Error occurred when deleting databucket";
    }
  },

  setLoadingDatabuckets({ commit }, payload) {
    commit("setLoadingDatabuckets", payload);
  },

  setDatabucketTotals({ commit }, payload) {
    commit("setDatabucketTotals", payload);
  }
};

const mutations = {
  setDatabuckets(state, payload) {
    state.databuckets = Array.isArray(payload) ? payload : [];
  },

  setDatabucketTotals(state, payload) {
    state.databucketTotals = Array.isArray(payload) ? payload : [];
  },

  setLoadingDatabuckets(state, payload) {
    state.loadingDatabuckets = Boolean(payload);
  },

  createDatabucket(state, payload) {
    if (payload && payload.id) {
      state.databuckets = [...state.databuckets, payload];
    }
  },

  updateDatabucket(state, payload) {
    if (payload && payload.id) {
      const index = state.databuckets.findIndex(item => item.id === payload.id);
      if (index !== -1) {
        state.databuckets = [
          ...state.databuckets.slice(0, index),
          { ...state.databuckets[index], ...payload },
          ...state.databuckets.slice(index + 1)
        ];
      }
    }
  },

  deleteDatabucket(state, databucketId) {
    state.databuckets = state.databuckets.filter(
      item => item.id !== databucketId
    );
  }
};

const getters = {
  databuckets(state) {
    return state.databuckets;
  },

  databucketTotals(state) {
    return state.databucketTotals;
  },

  loadingDatabuckets(state) {
    return state.loadingDatabuckets;
  },

  // Enhanced getters for better data access
  getDatabucketById: state => id => {
    return state.databuckets.find(databucket => databucket.id === id) || null;
  },

  getDatabucketByTitle: state => title => {
    return (
      state.databuckets.find(
        databucket => databucket.title?.toLowerCase() === title.toLowerCase()
      ) || null
    );
  },

  activeDatabuckets(state) {
    return state.databuckets.filter(
      databucket => databucket.status === "active"
    );
  },

  inactiveDatabuckets(state) {
    return state.databuckets.filter(
      databucket => databucket.status !== "active"
    );
  },

  databucketsByParticipantType: state => participantType => {
    return state.databuckets.filter(
      databucket => databucket.participantType === participantType
    );
  },

  redeemableDatabuckets(state) {
    return state.databuckets.filter(
      databucket => databucket.pointsRedeemable === true
    );
  },

  databucketsWithTargets(state) {
    return state.databuckets.filter(
      databucket => databucket.hasTargets === true
    );
  },

  databucketsCount(state) {
    return state.databuckets.length;
  },

  activeDatabucketsCount(state) {
    return state.databuckets.filter(
      databucket => databucket.status === "active"
    ).length;
  },

  databucketsOptions(state) {
    return state.databuckets.map(databucket => ({
      text: databucket.displayTitle || databucket.title,
      value: databucket.id
    }));
  },

  activeDatabucketsOptions(state) {
    return state.databuckets
      .filter(databucket => databucket.status === "active")
      .map(databucket => ({
        text: databucket.displayTitle || databucket.title,
        value: databucket.id
      }));
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};
